import { Injectable, inject } from '@angular/core';
import { FILE_LANE, UploadEngine, fontLaneMeta } from '@lite-workspace/uploader-core';
import { FontMetadataParserService } from '@lite-workspace/fonts';

/** The uploaded font file id + every name the face answers to (postScript / family / full). */
export interface UploadedFont {
  readonly fileId: string;
  readonly names: string[];
  // Kept apart from `names`: postScript/full identify THIS face, the family name is shared by every weight.
  readonly postScriptName?: string;
  readonly fontFamily?: string;
  readonly fullName?: string;
}

/**
 * Ports the legacy fc-designs font upload into the live app on `@lite-workspace/uploader-core` (the
 * `/uploader` barrel drags the legacy UI graph). Real metadata is parsed from the file (opentype.js)
 * and shipped with the upload so the backend registers the face under its true names.
 */
@Injectable({ providedIn: 'root' })
export class FontUploadService {
  private readonly engine = inject(UploadEngine);
  private readonly metadataParser = inject(FontMetadataParserService);

  async uploadFont(designId: string, file: File): Promise<UploadedFont> {
    const metadata = await this.metadataParser.extractFontMetadata(file);
    if (!metadata.isValid) throw new Error(`Invalid font file: ${file.name}`);

    const instances = (metadata.instances ?? []).map(instance => ({
      postScriptName: instance.postScriptName ?? '',
      fontFamily: instance.fontFamily,
      fullName: instance.fullName,
      fontStyle: instance.fontStyle,
      fontWeight: instance.fontWeight,
    }));
    const stored = await this.engine.uploadAndWait({
      lane: FILE_LANE,
      key: designId,
      file,
      meta: fontLaneMeta(designId, crypto.randomUUID(), {
        postScriptName: metadata.postScriptName ?? '',
        fontFamily: metadata.fontFamily,
        fullName: metadata.fullName,
        fontStyle: metadata.fontStyle,
        fontWeight: metadata.fontWeight,
        instances,
      }),
    });
    if (!stored) throw new Error('Font upload did not return a file id');

    const names = [metadata.postScriptName, metadata.fontFamily, metadata.fullName].filter((name): name is string => !!name);
    return {
      fileId: stored.id,
      names,
      postScriptName: metadata.postScriptName || undefined,
      fontFamily: metadata.fontFamily || undefined,
      fullName: metadata.fullName || undefined,
    };
  }
}
